import { useCallback, useEffect, useRef, useState } from "react";
import TokenService, { jwtConfig } from "services/TokenService";
import { timeInMs } from "utils/date-utils";
import { debugMode } from "utils/debug-utils";
import { decode, doNothing } from "utils/utils";

const sessionConfig = {
  timeoutInMs: timeInMs('00:30:00'),
  warningTimeInMs: timeInMs('00:05:00')
};

const useAuthTimeout = ({timeInMs, initialTimestamp = 0} = {}) => {
  const [isReached, setIsReached] = useState(false);
  const timeoutId = useRef(null);

  const configTimeout = useCallback(({timestamp, isCancel = false} = {}) => {
    timeoutId.current != null && clearTimeout(timeoutId.current);
    setIsReached(false);

    if (isCancel) {
      timeoutId.current = null;
      return;
    }
    
    const now = new Date().getTime();
    const delta = now - timestamp;
    const time = timestamp
      ? timeInMs - delta 
      : timeInMs;
    
    timeoutId.current = (
      setTimeout(() => setIsReached(true), time)
    );
  }, [timeInMs]);

  useEffect(() => {
    configTimeout({timestamp: initialTimestamp});
  }, [configTimeout]);
 

  const reset = useCallback(configTimeout, [configTimeout]);
  const cancel = useCallback(() => configTimeout({isCancel: true}), [configTimeout]);

  return [
    isReached,
    cancel,
    reset,
  ];
}


const useAuthToken = () => {
  const removeAuth = TokenService.removeAuthData;
  const [auth, setAuth] = useState(TokenService.getAuthData());
  const { jwt } = auth ?? {};

  const [sessionTimeout, cancelSessionTimeout, resetSessionTimeout] = useAuthTimeout({
    timeInMs: sessionConfig.timeoutInMs,
    initialTimestamp: auth?.timestamp
  });

  const [sessionWarning, cancelSessionWarning, resetSessionWarning] = useAuthTimeout({
    timeInMs: sessionConfig.timeoutInMs - sessionConfig.warningTimeInMs,
    initialTimestamp: auth?.timestamp
  });

  useEffect(() => {
    const authDataSubscription = TokenService.authDataChanges$.subscribe(
      (tokenServiceAuthData) => {
        debugMode(() => console.log('auth changed', tokenServiceAuthData));
        setAuth(tokenServiceAuthData);
      }
    );

    const watchAuthChangesInAnotherWindows = (e) => { 
      const {key, newValue } = e ?? {};
      try {
        const authDataChangedByAnotherWindow = JSON.parse(newValue);
        if (key === jwtConfig.key) {
          debugMode(() => console.log('auth changed by antoher window', authDataChangedByAnotherWindow));
          setAuth(authDataChangedByAnotherWindow);
        }
      } catch (e) {
        console.warn('Autenticação inválida ignorada');
      }
    };

    window.addEventListener('storage', watchAuthChangesInAnotherWindows, false);

    return () => {
      authDataSubscription.unsubscribe();
      window.removeEventListener('storage', watchAuthChangesInAnotherWindows, false);
    };
  }, []);

  const payload = useCallback(() => {
    try {
      return decode(jwt, jwtConfig.prefix) || decode(jwt);
    } catch (e) {
      console.warn(e);
      return null;
    }
  }, [jwt]);

  const getCredentials = useCallback(() => payload()?.jsonSiconfi, [payload]);

  const isAuthenticated = getCredentials()?.cpf != null;

  useEffect(() => {
    if (auth && isAuthenticated) {
      resetSessionTimeout({timestamp: auth?.timestamp});
      resetSessionWarning({timestamp: auth?.timestamp});
    } else {
      cancelSessionTimeout();
      cancelSessionWarning();
    }
  }, [auth, isAuthenticated, resetSessionTimeout, resetSessionWarning, cancelSessionTimeout, cancelSessionWarning])

  useEffect(() => {
    if (sessionTimeout && isAuthenticated) {
      removeAuth();
    }
  }, [sessionTimeout, isAuthenticated, removeAuth])

  return {
    jwt,
    removeAuth,
    getCredentials,
    sessionWarning,
    sessionExpiresAt: auth?.timestamp ? auth.timestamp + sessionConfig.timeoutInMs : null,
  }
}

export default useAuthToken;